// ── Error classifier ─────────────────────────────────────────────────────
// Turns a raw error (Supabase error object, fetch error, native Error,
// or plain string) into a { kind, message, canRetry } triple that the UI
// can display. The goal is to give users an actionable hint about what
// went wrong — network vs auth vs validation vs server — instead of the
// raw backend string.
//
// Usage:
//   const { message, canRetry } = classifyError(err)
//   setError(message)
//   if (canRetry) showRetryButton()

// Postgres / Supabase error codes we recognize.
const PG_UNIQUE_VIOLATION = '23505'
const PG_NOT_NULL_VIOLATION = '23502'
const PG_CHECK_VIOLATION = '23514'
const PG_FOREIGN_KEY_VIOLATION = '23503'
const PG_INVALID_TEXT_REPR = '22P02'
const PGRST_NO_ROWS = 'PGRST116'

// Heuristic: an error is offline if the browser says so OR the fetch
// failed with a TypeError before a response came back.
function looksOffline(err) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true
  if (!err) return false
  const msg = String(err.message || err).toLowerCase()
  return (
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('load failed') ||
    msg.includes('fetch failed') ||
    msg.includes('network request failed')
  )
}

function statusFromError(err) {
  if (!err) return null
  if (typeof err.status === 'number') return err.status
  if (typeof err.statusCode === 'number') return err.statusCode
  if (typeof err.code === 'number') return err.code
  return null
}

export function classifyError(err) {
  // null/undefined → unknown (don't crash the UI)
  if (err == null) {
    return { kind: 'unknown', message: 'Something went wrong. Please try again.', canRetry: true }
  }

  // Already-classified error — pass through
  if (err.kind && err.message) return err

  // Plain string — treat as unknown
  if (typeof err === 'string') {
    return { kind: 'unknown', message: err, canRetry: true }
  }

  const code = err.code || err.error_code || null
  const status = statusFromError(err)
  const rawMsg = String(err.message || err.error_description || err.error || '').trim()

  // Network / offline
  if (looksOffline(err)) {
    return {
      kind: 'network',
      message: "You're offline. Check your connection and try again.",
      canRetry: true,
    }
  }

  // Auth
  if (status === 401 || status === 403) {
    return {
      kind: 'auth',
      message: 'Your session expired. Please sign in again.',
      canRetry: false,
    }
  }
  const lowered = rawMsg.toLowerCase()
  if (
    lowered.includes('jwt expired') ||
    lowered.includes('invalid jwt') ||
    lowered.includes('invalid credentials') ||
    lowered.includes('not authenticated')
  ) {
    return {
      kind: 'auth',
      message: lowered.includes('invalid credentials')
        ? 'Wrong email or password. Please try again.'
        : 'Your session expired. Please sign in again.',
      canRetry: false,
    }
  }

  // Rate limit
  if (status === 429) {
    return {
      kind: 'rateLimit',
      message: 'Too many requests. Please wait a moment and try again.',
      canRetry: true,
    }
  }

  // Validation (Postgres constraint or bad input)
  if (
    status === 400 || status === 422 ||
    code === PG_NOT_NULL_VIOLATION ||
    code === PG_CHECK_VIOLATION ||
    code === PG_INVALID_TEXT_REPR ||
    code === PG_FOREIGN_KEY_VIOLATION
  ) {
    return {
      kind: 'validation',
      message: 'Some fields are missing or invalid. Please check and try again.',
      canRetry: false,
    }
  }

  // Conflict / duplicate
  if (status === 409 || code === PG_UNIQUE_VIOLATION) {
    return {
      kind: 'conflict',
      message: 'That already exists. Please use a different value.',
      canRetry: false,
    }
  }

  // Not found
  if (status === 404 || code === PGRST_NO_ROWS) {
    return {
      kind: 'notFound',
      message: "We couldn't find what you were looking for.",
      canRetry: false,
    }
  }

  // Server-side
  if (status && status >= 500) {
    return {
      kind: 'server',
      message: "Something went wrong on our end. We're looking into it — please try again in a moment.",
      canRetry: true,
    }
  }

  // Fallback — keep the raw message if it's short and looks safe to show,
  // otherwise use a generic string.
  if (rawMsg && rawMsg.length <= 140 && !rawMsg.startsWith('{')) {
    return { kind: 'unknown', message: rawMsg, canRetry: true }
  }
  return { kind: 'unknown', message: 'Something went wrong. Please try again.', canRetry: true }
}

// Convenience wrapper used by hooks: return just the message string.
export function errorMessageFor(err) {
  return classifyError(err).message
}
